/*
 * File: authentication.service.ts
 * Project: rast-app
 * Create Date: Friday, 1st August 2025
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Friday, 31st July 2026 3:10:00 pm
 * Modified by: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Copyright (c) 2026 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  GoogleSignUpRequestDto,
  LoginResponseDto,
  SignUpRequestDto,
  SignUpResponseDto,
} from 'src/app/types/authentication.types';
import {
  ResetPasswordRequestDto,
  ValidateResetPasswordTokenRequestDto,
} from 'src/app/types/forgotPasswordTypes';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiAuthenticationService {
  constructor(private readonly http: HttpClient) {}

  signUpUser(body: SignUpRequestDto): Observable<SignUpResponseDto> {
    return this.http.post<SignUpResponseDto>(
      `${environment.apiUrl}/user`,
      body,
    );
  }

  /**
   * Call API to login with encrypted auth string
   * @param encryptedAuth Encrypted string of btoa(username:password)
   * @returns Observable<LoginResponseDto>
   */
  login(encryptedAuth: string) {
    return this.http.post<LoginResponseDto>(
      `${environment.apiUrl}/login`,
      { application: 'RAST-Platform', version: `${environment.version}` },
      { headers: { authorization: `Basic ${encryptedAuth}` } },
    );
  }

  refreshToken(refreshToken: string) {
    return this.http.post<LoginResponseDto>(`${environment.apiUrl}/refresh`, {
      refreshToken,
    });
  }

  generateResetPasswordToken(cpf: string) {
    return this.http.post<LoginResponseDto>(
      `${environment.apiUrl}/user/forgot-password`,
      { UserCpf: cpf },
    );
  }

  validateResetPasswordToken(
    validateTokenData: ValidateResetPasswordTokenRequestDto,
  ) {
    return this.http.post(
      `${environment.apiUrl}/user/validate-reset-password-token`,
      validateTokenData,
    );
  }

  resetPassword(resetPasswordData: ResetPasswordRequestDto) {
    return this.http.post(
      `${environment.apiUrl}/user/reset-password`,
      resetPasswordData,
    );
  }

  loginGoogle(body: GoogleSignUpRequestDto): Observable<LoginResponseDto> {
    const firebaseIdToken = body.firebaseToken;

    return this.http.post<LoginResponseDto>(
      `${environment.apiUrl}/sso-google-callback`,
      body,
      {
        headers: { 'X-Firebase-Token': firebaseIdToken },
      },
    );
  }
}
